import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(3deg);
  }
  100% {
    transform: translateY(0px) rotate(0deg);
  }
`;

const rotateSlow = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
`;

const ripple = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.3);
    transform: scale(1);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(255, 255, 255, 0);
    transform: scale(1.05);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
    transform: scale(1);
  }
`;

const raindrop = keyframes`
  0% {
    transform: translateY(-10px);
    opacity: 0;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    transform: translateY(100px);
    opacity: 0;
  }
`;

const snowfall = keyframes`
  0% {
    transform: translateY(-10px) rotate(0deg);
    opacity: 0;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    transform: translateY(100px) rotate(360deg);
    opacity: 0;
  }
`;

const wavyMovement = keyframes`
  0% {
    transform: translateX(0) translateY(0);
  }
  50% {
    transform: translateX(20px) translateY(-10px);
  }
  100% {
    transform: translateX(0) translateY(0);
  }
`;

// Styled components
const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  min-height: 600px;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: linear-gradient(135deg, ${props => props.theme.colors.primaryDark} 0%, ${props => props.theme.colors.primary} 100%);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 40%);
  }
`;

const ParticlesBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
`;

// Define a specific animation css builder function for particles
const buildParticleAnimation = (animation, duration, delay) => css`
  ${animation} ${duration} ease-in-out ${delay} infinite
`;

// Using transient props with $ prefix for styled-components v6
const Particle = styled.div`
  position: absolute;
  width: ${props => props.$size || '4px'};
  height: ${props => props.$size || '4px'};
  background-color: ${props => props.$color || 'rgba(255, 255, 255, 0.3)'};
  border-radius: 50%;
  top: ${props => props.$top || '10%'};
  left: ${props => props.$left || '10%'};
  animation: ${props => props.$animation ? 
    buildParticleAnimation(props.$animation, props.$duration || '3s', props.$delay || '0s') 
    : css`${fadeIn} 3s ease-in-out 0s infinite`};
  opacity: ${props => props.$opacity || 0.5};
  z-index: ${props => props.$zIndex || 1};
  
  &::before {
    content: ${props => props.$content || '""'};
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    text-align: center;
    padding-top: 80px;
  }
`;

const HeroText = styled.div`
  flex: 1;
  animation: ${props => css`${fadeIn} 1s ease forwards`};
  animation-delay: 0.3s;
  opacity: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-bottom: 3rem;
    order: 2;
  }
`;

const HeroHeading = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: white;
  line-height: 1.2;
  
  span {
    color: ${props => props.theme.colors.accent};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const HeroSubheading = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  max-width: 550px;
  line-height: 1.6;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.a`
  background-color: white;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const SecondaryButton = styled.a`
  background-color: transparent;
  color: white;
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  border: 2px solid white;
  transition: transform 0.3s ease, background-color 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-3px);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const HeroVisual = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  animation: ${props => css`${fadeIn} 1s ease forwards`};
  animation-delay: 0.6s;
  opacity: 0;
  position: relative;
  max-width: 500px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    order: 1;
    margin-bottom: 2rem;
  }
`;

const GlobeContainer = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    height: 300px;
  }
`;

const Globe = styled.div`
  width: 300px;
  height: 300px;
  background: radial-gradient(circle at 70% 70%, #e8f4fc 0%, #cae8fa 50%, #a1d6f7 100%);
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 80px rgba(161, 214, 247, 0.6);
  overflow: hidden;
  animation: ${props => css`${float} 6s ease-in-out infinite`};
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    background-image: url('/wt-logo-original.webp'), url('/wt-logo-original.png');
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    opacity: 0.15;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 240px;
    height: 240px;
  }
`;

const GlobeOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 100%);
    border-radius: 50%;
  }
`;

const Landmass = styled.div`
  position: absolute;
  background-color: rgba(144, 238, 144, 0.3);
  border-radius: 40%;
  filter: blur(5px);
  
  &:nth-child(1) {
    top: 30%;
    left: 20%;
    width: 80px;
    height: 50px;
    transform: rotate(15deg);
  }
  
  &:nth-child(2) {
    top: 20%;
    right: 30%;
    width: 50px;
    height: 70px;
    transform: rotate(-20deg);
  }
  
  &:nth-child(3) {
    bottom: 30%;
    left: 35%;
    width: 70px;
    height: 40px;
    transform: rotate(30deg);
  }
  
  &:nth-child(4) {
    bottom: 40%;
    right: 25%;
    width: 60px;
    height: 60px;
    transform: rotate(-10deg);
  }
`;

const RotatingGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: 
    linear-gradient(transparent 0%, transparent 49.5%, rgba(255,255,255,0.1) 50%, transparent 50.5%, transparent 100%),
    linear-gradient(90deg, transparent 0%, transparent 49.5%, rgba(255,255,255,0.1) 50%, transparent 50.5%, transparent 100%);
  background-size: 60px 60px;
  animation: ${props => css`${rotateSlow} 120s linear infinite`};
`;

// Updated with transient props for v6
const WeatherIcon = styled.div.attrs(props => ({
  style: {
    top: props.$top || '0',
    left: props.$left || '0'
  },
  'data-emoji': props.$emoji
}))`
  position: absolute;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  animation: ${props => css`${ripple} 3s ease-in-out infinite, ${wavyMovement} 6s ease-in-out infinite`};
  animation-delay: ${props => props.$delay || '0s'}, ${props => props.$waveDelay || '0s'};
  z-index: 2;
  
  &::before {
    content: '${props => props['data-emoji'] || '☀️'}';
    font-size: 30px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 40px;
    height: 40px;
    
    &::before {
      font-size: 24px;
    }
  }
`;

const WeatherEffect = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 50%;
  pointer-events: none;
  overflow: hidden;
  z-index: 3;
`;

// Updated with transient props for v6
const RainContainer = styled.div.attrs(props => ({
  style: {
    opacity: props.$active ? 0.7 : 0
  }
}))`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 0.5s ease;
`;

// Updated with transient props for v6
const SnowContainer = styled.div.attrs(props => ({
  style: {
    opacity: props.$active ? 0.7 : 0
  }
}))`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 0.5s ease;
`;

// Updated with transient props for v6
const WeatherMarker = styled.div.attrs(props => ({
  style: {
    top: props.$top || '0',
    left: props.$left || '0'
  }
}))`
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.15);
  animation: ${props => css`${pulse} 3s ease-in-out infinite`};
  animation-delay: ${props => props.$delay || '0s'};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 60px;
    height: 60px;
  }
`;

const ParticlesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

// Color gradient for weather effects
const gradientColors = {
  clear: 'linear-gradient(135deg, #4ec0f9 0%, #89d6fb 100%)',
  cloudy: 'linear-gradient(135deg, #b4d2e6 0%, #e6eef4 100%)',
  rainy: 'linear-gradient(135deg, #4a6fa5 0%, #8aa5c9 100%)',
  snowy: 'linear-gradient(135deg, #7a9cc2 0%, #c8d8eb 100%)'
};

// Hero component
const Hero = () => {
  const { translate } = useLanguage();
  const particlesRef = useRef(null);
  const [currentWeather, setCurrentWeather] = useState('clear');
  
  // Initialize particles on component mount
  useEffect(() => {
    const container = particlesRef.current;
    // Create particles for a subtle animated background
    for (let i = 0; i < 50; i++) {
      const particle = document.createElement('div');
      
      // Styling the particle
      particle.style.position = 'absolute';
      particle.style.width = `${Math.random() * 5 + 1}px`;
      particle.style.height = particle.style.width;
      particle.style.backgroundColor = 'rgba(255, 255, 255, 0.5)';
      particle.style.borderRadius = '50%';
      
      // Random position
      particle.style.top = `${Math.random() * 100}%`;
      particle.style.left = `${Math.random() * 100}%`;
      
      // Animation properties
      particle.style.opacity = Math.random() * 0.5 + 0.1;
      
      const animationDuration = Math.random() * 20 + 10;
      const animationDelay = Math.random() * 5;
      
      // Use a CSS keyframe name directly for dynamic DOM elements
      // Instead of trying to reference the keyframe object directly
      particle.style.animationName = 'float';
      particle.style.animationDuration = `${animationDuration}s`;
      particle.style.animationTimingFunction = 'ease-in-out';
      particle.style.animationDelay = `${animationDelay}s`;
      particle.style.animationIterationCount = 'infinite';
      
      // Add particle to container
      if (container) {
        container.appendChild(particle);
      }
    }
    
    // Cleanup function
    return () => {
      if (container) {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      }
    };
  }, []);
  
  // Cycle through weather effects
  useEffect(() => {
    const weathers = ['clear', 'cloudy', 'rainy', 'snowy'];
    let index = 0;
    
    const interval = setInterval(() => {
      index = (index + 1) % weathers.length;
      setCurrentWeather(weathers[index]);
    }, 8000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Generate raindrops for rain animation - with transient props
  const renderRaindrops = () => {
    const drops = [];
    for (let i = 0; i < 20; i++) {
      const left = `${Math.random() * 100}%`;
      const delay = Math.random() * 2;
      const duration = 1 + Math.random() * 1;
      
      drops.push(
        <Particle 
          key={`rain-${i}`}
          $top="0%"
          $left={left}
          $size="2px"
          $color="rgba(120, 180, 255, 0.6)"
          $animation={raindrop}
          $duration={`${duration}s`}
          $delay={`${delay}s`}
          $zIndex={4}
        />
      );
    }
    return drops;
  };
  
  // Generate snowflakes for snow animation - with transient props
  const renderSnowflakes = () => {
    const flakes = [];
    for (let i = 0; i < 15; i++) {
      const left = `${Math.random() * 100}%`;
      const size = 3 + Math.random() * 4;
      const delay = Math.random() * 2;
      const duration = 2 + Math.random() * 2;
      
      flakes.push(
        <Particle 
          key={`snow-${i}`}
          $top="0%"
          $left={left}
          $size={`${size}px`}
          $color="rgba(255, 255, 255, 0.8)"
          $animation={snowfall}
          $duration={`${duration}s`}
          $delay={`${delay}s`}
          $zIndex={4}
        />
      );
    }
    return flakes;
  };
  
  // Weather icons positions around the globe
  const weatherIcons = [
    { top: '20%', left: '30%', emoji: '☀️', delay: '0s', waveDelay: '0s' },
    { top: '70%', left: '20%', emoji: '🌧️', delay: '0.3s', waveDelay: '1s' },
    { top: '30%', left: '80%', emoji: '⛅', delay: '0.6s', waveDelay: '2s' },
    { top: '60%', left: '70%', emoji: '❄️', delay: '0.9s', waveDelay: '0.5s' },
    { top: '40%', left: '60%', emoji: '🌪️', delay: '1.2s', waveDelay: '1.5s' },
  ];
  
  return (
    <HeroSection>
      <ParticlesBackground ref={particlesRef} />
      <HeroContent>
        <HeroText>
          <HeroHeading>
            {translate('hero.heading1', 'Community-Driven ')}
            <span>{translate('hero.headingHighlight', 'Weather')}</span> 
            {translate('hero.heading2', ' Reporting')}
          </HeroHeading>
          <HeroSubheading>
            {translate('hero.subheading', 'Share and discover real-time weather conditions through an interactive community map. WeatherTeller lets you report, validate, and explore weather data from around the world.')}
          </HeroSubheading>
          <ButtonContainer>
            <PrimaryButton href="#download">
              {translate('hero.primaryCta', 'Get Started')}
            </PrimaryButton>
            <SecondaryButton href="#manual">
              {translate('hero.secondaryCta', 'Learn More')}
            </SecondaryButton>
          </ButtonContainer>
        </HeroText>
        
        <HeroVisual>
          <GlobeContainer>
            <Globe style={{ background: gradientColors[currentWeather] }}>
              <GlobeOverlay>
                <RotatingGrid />
                <Landmass />
                <Landmass />
                <Landmass />
                <Landmass />
              </GlobeOverlay>
              
              <WeatherEffect>
                <RainContainer $active={currentWeather === 'rainy'}>
                  {renderRaindrops()}
                </RainContainer>
                
                <SnowContainer $active={currentWeather === 'snowy'}>
                  {renderSnowflakes()}
                </SnowContainer>
              </WeatherEffect>
            </Globe>
            
            {weatherIcons.map((icon, index) => (
              <React.Fragment key={index}>
                <WeatherMarker 
                  $top={icon.top} 
                  $left={icon.left} 
                  $delay={icon.delay}
                />
                <WeatherIcon 
                  $top={icon.top} 
                  $left={icon.left} 
                  $emoji={icon.emoji}
                  $delay={icon.delay}
                  $waveDelay={icon.waveDelay}
                />
              </React.Fragment>
            ))}
          </GlobeContainer>
        </HeroVisual>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero;